import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { Segmented } from '../../components/Segmented';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import type { ComboboxOption } from '../../components/Combobox';
import { CustomerCombobox } from '../crm/CustomerCombobox';
import { useCustomerFile } from '../crm/queries';
import type { InboundMessage, InboundStatus } from './api';
import { useAssignInbound, useInboundMessage, useInboundQueue, useRejectInbound } from './queries';
import { Select } from '../../components/Select';

/**
 * Die Anfrage-Queue -- F5-03.
 *
 * **Was hier steht, war vorher unsichtbar.** Eine Mail von einem Absender,
 * den kein Kontakt im CRM traegt, laesst sich bei einem Mandanten mit mehr
 * als einem Kunden nicht zu einem Vorgang machen: Das System weiss nicht, fuer
 * wen sie laeuft, und raet nicht. Bis zu diesem Feature endete sie damit im
 * Nichts -- der Abruf vermerkte einen Fehler, die Kennung stand in
 * ``inbound_messages``, und beim naechsten Abruf kam sie deshalb nicht wieder.
 * Am 15.09.2026 lagen 141 solcher Nachrichten aus fuenf Tagen.
 *
 * **Zwei Wege heraus, und beide enden sichtbar.** Zuweisen macht den Vorgang,
 * den der Abruf nicht machen konnte. Ablehnen sagt dem Absender ab -- und das
 * ist der Punkt, an dem diese Maske sich von einer Papierkorbliste
 * unterscheidet: Wer nicht antwortet, laesst jemanden warten, der glaubt,
 * seine Anfrage sei angekommen.
 *
 * **Der Altbestand hat keinen Text.** Die Spalten dafuer entstanden erst mit
 * Migration 0048; was davor einging, liegt nur noch im Postfach. Die Zeile
 * sagt das ausdruecklich, statt eine leere Flaeche zu zeigen -- die sieht aus
 * wie eine leere Mail.
 *
 * **Die ganze Mail ist einen Klick entfernt.** Die Absenderadresse verraet den
 * Kunden nicht -- sonst stuende die Mail nicht hier --, und die Vorschau
 * hoert oft genau vor der Signatur auf. Der Betreff oeffnet die Nachricht
 * vollstaendig; der Zuweisungsdialog zeigt sie ebenfalls, damit beim Suchen
 * des Kunden niemand zwischen zwei Fenstern hin- und herspringen muss.
 */
export function InboundQueuePanel({ heading = true }: { heading?: boolean }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<InboundStatus>('open');
  const [reading, setReading] = useState<InboundMessage | null>(null);
  const [assigning, setAssigning] = useState<InboundMessage | null>(null);
  const [rejecting, setRejecting] = useState<InboundMessage | null>(null);

  const queue = useInboundQueue(status);
  const entries = queue.data?.items ?? [];

  return (
    <section className="card">
      {/* Als Reiter in der E-Mail-Anbindung braucht die Liste eine
          Ueberschrift -- dort stehen sechs Panele nebeneinander. Auf der
          eigenen Seite steht derselbe Satz schon im Seitenkopf, und zweimal
          untereinander sieht er aus wie ein Fehler. */}
      {heading ? (
        <div className="chart-card__head">
          <h2>{t('inbound.title')}</h2>
          <p className="chart-card__hint">{t('inbound.hint')}</p>
        </div>
      ) : null}

      <Segmented
        ariaLabel={t('inbound.title')}
        value={status}
        onChange={setStatus}
        options={[
          { value: 'open', label: t('inbound.open') },
          { value: 'assigned', label: t('inbound.assigned') },
          { value: 'rejected', label: t('inbound.rejected') },
        ]}
      />

      {queue.isPending ? <SkeletonTable columns={4} /> : null}
      {queue.isError ? <ErrorNotice error={queue.error} /> : null}

      {queue.data && entries.length === 0 ? (
        <EmptyState
          icon="mail"
          title={t(status === 'open' ? 'inbound.emptyOpen' : 'inbound.emptyArchive')}
          hint={status === 'open' ? t('inbound.emptyOpenHint') : undefined}
        />
      ) : null}

      {entries.length > 0 ? (
        <div className="table-wrapper table-wrapper--tall">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('mailField.from')}</th>
                <th scope="col">{t('mailField.subject')}</th>
                <th scope="col">{t('inbound.received')}</th>
                <th scope="col">
                  {status === 'open' ? t('tickets.actions') : t('inbound.outcome')}
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    {entry.from_address ?? t('inbound.withoutSender')}
                    {/* Der Auszug steht unter dem Absender und nicht in einer
                        eigenen Spalte: Er ist zwei Zeilen lang und haette
                        eine Spalte gesprengt, die daneben Betreffe traegt. */}
                    <div className="table__muted inbound__preview">
                      {entry.body_preview ? (
                        <>
                          {entry.body_preview.slice(0, 220)}
                          {entry.body_preview.length > 220 || entry.body_truncated ? ' …' : ''}
                        </>
                      ) : (
                        <em>{t('inbound.noBody')}</em>
                      )}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="inbound__subject"
                      title={t('inbound.read')}
                      onClick={() => {
                        setReading(entry);
                      }}
                    >
                      {entry.subject ?? t('inbound.withoutSubject')}
                    </button>
                  </td>
                  <td className="table__muted">
                    {entry.received_at
                      ? formatDateTime(entry.received_at, i18n.language)
                      : formatDateTime(entry.created_at, i18n.language)}
                  </td>
                  <td>
                    {status === 'open' ? (
                      <div className="row">
                        <Button
                          variant="primary"
                          onClick={() => {
                            setAssigning(entry);
                          }}
                        >
                          {t('inbound.assign')}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setRejecting(entry);
                          }}
                        >
                          {t('inbound.reject')}
                        </Button>
                      </div>
                    ) : (
                      <Outcome entry={entry} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* **Die Deckelung wird gesagt und nicht verschwiegen.** Die Liste holt
          zweihundert Anfragen; sind es mehr, zeigt eine stillschweigend
          gekuerzte Liste so aus wie eine vollstaendige. In der offenen Liste
          loest sich das von selbst -- was bearbeitet ist, verlaesst sie --,
          im Archiv nicht. */}
      {queue.data?.has_more ? <p className="field__hint">{t('inbound.more')}</p> : null}

      {reading ? (
        <MessageDialog
          entry={reading}
          onClose={() => {
            setReading(null);
          }}
          onAssign={() => {
            setReading(null);
            setAssigning(reading);
          }}
          onReject={() => {
            setReading(null);
            setRejecting(reading);
          }}
        />
      ) : null}

      {assigning ? (
        <AssignDialog
          entry={assigning}
          onClose={() => {
            setAssigning(null);
          }}
        />
      ) : null}

      {rejecting ? (
        <RejectDialog
          entry={rejecting}
          onClose={() => {
            setRejecting(null);
          }}
        />
      ) : null}
    </section>
  );
}

/** Was aus einer Anfrage geworden ist -- im Archiv die einzige Frage. */
function Outcome({ entry }: { entry: InboundMessage }) {
  const { t, i18n } = useTranslation();

  return (
    <div>
      {entry.status === 'assigned' ? (
        <Badge tone="success">{entry.ticket_number ?? t('inbound.assigned')}</Badge>
      ) : (
        <Badge tone="neutral">{t('inbound.rejected')}</Badge>
      )}
      {entry.reject_reason ? <div className="table__muted">{entry.reject_reason}</div> : null}
      {entry.resolved_at ? (
        <div className="table__muted">{formatDateTime(entry.resolved_at, i18n.language)}</div>
      ) : null}
    </div>
  );
}

/**
 * Der Kopf und der vollstaendige Text einer Anfrage.
 *
 * **Klartext in einem ``pre``-Block und kein HTML.** Die Nachricht kommt von
 * aussen und ist ungeprueft; React maskiert den Text, fremdes Markup kommt so
 * nicht in die Oberflaeche. Umbrueche und Einrueckungen bleiben stehen -- eine
 * Signatur ist oft nur an ihrer Zeilenform zu erkennen.
 */
function MessageBody({ entry }: { entry: InboundMessage }) {
  const { t, i18n } = useTranslation();
  const message = useInboundMessage(entry.id);
  const receivedAt = entry.received_at ?? entry.created_at;

  return (
    <div className="inbound__message">
      <dl className="inbound__headers">
        <dt>{t('mailField.from')}</dt>
        <dd>{entry.from_address ?? t('inbound.withoutSender')}</dd>
        <dt>{t('mailField.subject')}</dt>
        <dd>{entry.subject ?? t('inbound.withoutSubject')}</dd>
        <dt>{t('inbound.received')}</dt>
        <dd>{formatDateTime(receivedAt, i18n.language)}</dd>
      </dl>

      {message.isPending ? <SkeletonTable columns={1} /> : null}
      {message.isError ? <ErrorNotice error={message.error} /> : null}
      {message.data ? (
        message.data.body_text ? (
          <pre className="inbound__body" tabIndex={0} aria-label={t('mailField.body')}>
            {message.data.body_text}
          </pre>
        ) : (
          <p className="field__hint">
            <em>{t('inbound.noBody')}</em>
          </p>
        )
      ) : null}
    </div>
  );
}

/** Die Anfrage lesen -- mit den beiden Entscheidungen, solange sie offen ist. */
function MessageDialog({
  entry,
  onClose,
  onAssign,
  onReject,
}: {
  entry: InboundMessage;
  onClose: () => void;
  onAssign: () => void;
  onReject: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content dialog__content--wide">
          <Dialog.Title asChild>
            <h2>{t('inbound.readTitle')}</h2>
          </Dialog.Title>
          <Dialog.Description className="field__hint">{t('inbound.readHint')}</Dialog.Description>

          <MessageBody entry={entry} />

          <div className="row">
            {entry.status === 'open' ? (
              <>
                <Button variant="primary" onClick={onAssign}>
                  {t('inbound.assign')}
                </Button>
                <Button variant="ghost" onClick={onReject}>
                  {t('inbound.reject')}
                </Button>
              </>
            ) : null}
            <Button variant="ghost" onClick={onClose}>
              {t('app.close')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Kunde zuweisen.
 *
 * **Die Kundenauswahl sucht, sie listet nicht.** Achtunddreissig Kunden sind
 * noch eine Auswahlliste; dreihundert sind es nicht mehr. Ein Feld genuegt:
 * Tippen grenzt ein, ein Klick waehlt -- vorher standen Suchfeld und Liste
 * getrennt untereinander.
 *
 * **Der Haken fuer den Kontakt steht aus.** Wer einmal schreibt, ist noch kein
 * Ansprechpartner -- ein Bestand, der sich beim Abarbeiten einer Warteschlange
 * von selbst fuellt, ist nach einem Monat unbrauchbar. Wer ihn setzt, sorgt
 * dafuer, dass die naechste Mail derselben Adresse ohne Zutun ankommt.
 */
function AssignDialog({ entry, onClose }: { entry: InboundMessage; onClose: () => void }) {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<ComboboxOption | null>(null);
  const customerId = customer?.id ?? '';
  const [contactId, setContactId] = useState('');
  const [createContact, setCreateContact] = useState(false);

  const file = useCustomerFile(customerId);
  const assign = useAssignInbound();

  const contacts = file.data?.contacts ?? [];

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content dialog__content--wide">
          <Dialog.Title asChild>
            <h2>{t('inbound.assignTitle')}</h2>
          </Dialog.Title>
          <Dialog.Description className="field__hint">
            {t('inbound.assignSubtitle', {
              from: entry.from_address ?? t('inbound.withoutSender'),
            })}
          </Dialog.Description>

          {/* Die Mail steht im Dialog und nicht nur dahinter: Der Kunde
              ergibt sich aus ihrem Text, und gesucht wird er hier. */}
          <MessageBody entry={entry} />

          <div className="field">
            <label className="field__label" htmlFor="inbound-customer">
              {t('crm.customer')}
            </label>
            <CustomerCombobox
              id="inbound-customer"
              value={customer}
              onChange={(next) => {
                setCustomer(next);
                setContactId('');
              }}
              placeholder={t('inbound.searchCustomer')}
            />
          </div>

          {customerId !== '' && contacts.length > 0 ? (
            <div className="field">
              <label className="field__label" htmlFor="inbound-contact">
                {t('crm.contacts')}
              </label>
              <Select
                id="inbound-contact"
                className="field__control"
                value={contactId}
                onChange={(event) => {
                  setContactId(event.target.value);
                }}
              >
                <option value="">{t('inbound.noContact')}</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.first_name} {contact.last_name}
                    {contact.email ? ` (${contact.email})` : ''}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}

          <label className="field__checkbox">
            <input
              type="checkbox"
              checked={createContact}
              onChange={(event) => {
                setCreateContact(event.target.checked);
              }}
            />
            {t('inbound.createContact')}
          </label>
          <span className="field__hint">{t('inbound.createContactHint')}</span>

          {assign.isError ? <ErrorNotice error={assign.error} /> : null}

          <div className="row">
            <Button
              variant="primary"
              disabled={customerId === ''}
              busy={assign.isPending}
              onClick={() => {
                assign.mutate(
                  {
                    inboundId: entry.id,
                    payload: {
                      customer_id: customerId,
                      contact_id: contactId === '' ? null : contactId,
                      create_contact: createContact,
                    },
                  },
                  { onSuccess: onClose },
                );
              }}
            >
              {t('inbound.assign')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('app.cancel')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Ablehnen.
 *
 * **Der Hinweis auf die Mail steht ueber dem Knopf und nicht darunter.** Die
 * Ablehnung schickt eine Absage an einen Menschen; wer das erst hinterher
 * erfaehrt, hat es nicht entschieden, sondern ausgeloest.
 */
function RejectDialog({ entry, onClose }: { entry: InboundMessage; onClose: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const reject = useRejectInbound();

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2>{t('inbound.rejectTitle')}</h2>
          </Dialog.Title>
          <Dialog.Description className="field__hint">
            {entry.from_address
              ? t('inbound.rejectSubtitle', { from: entry.from_address })
              : t('inbound.rejectWithoutSender')}
          </Dialog.Description>

          <div className="field">
            <label className="field__label" htmlFor="inbound-reason">
              {t('inbound.reason')}
            </label>
            <input
              id="inbound-reason"
              className="field__control"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <span className="field__hint">{t('inbound.reasonHint')}</span>
          </div>

          {reject.isError ? <ErrorNotice error={reject.error} /> : null}

          <div className="row">
            <Button
              variant="primary"
              busy={reject.isPending}
              onClick={() => {
                reject.mutate(
                  {
                    inboundId: entry.id,
                    payload: { reason: reason.trim() === '' ? null : reason.trim() },
                  },
                  { onSuccess: onClose },
                );
              }}
            >
              {t('inbound.reject')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('app.cancel')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
